import { useEffect, useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { products } from './products.ts';
import { SCREENS } from './screens/index.tsx';
import { BrowserFrame } from './BrowserFrame.tsx';

// How long each app holds before the showcase advances. WapSign plays a full
// multi-step send-to-sign flow, so it needs longer; the others show a single
// screen. Keyed by product key, with a default fallback.
const HOLD_MS: Record<string, number> = {
  wapsign: 12500,
  wapmeet: 9500,
  wapstat: 8000,
};
const DEFAULT_HOLD = 8000;

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduce(m.matches);
    sync();
    m.addEventListener('change', sync);
    return () => m.removeEventListener('change', sync);
  }, []);
  return reduce;
}

/**
 * The hero centrepiece — an interactive, auto-cycling showcase of every app.
 * The mockup rotates through every product screen; tabs switch between them,
 * a progress bar tracks the cycle, and hovering pauses it so the screen can be
 * read. Reduced-motion stops the auto-advance and transitions.
 */
export function AppShowcase(): JSX.Element {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduce = usePrefersReducedMotion();

  const current = products[active]!; // active is always a valid index (mod length)
  const hold = HOLD_MS[current.key] ?? DEFAULT_HOLD;

  useEffect(() => {
    if (paused || reduce) return;
    const t = setTimeout(() => setActive((i) => (i + 1) % products.length), hold);
    return () => clearTimeout(t);
  }, [active, paused, reduce, hold]);

  const Screen = SCREENS[current.key];
  const host = current.url.replace(/^https?:\/\//, '');

  return (
    <div
      className="app-showcase"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="showcase-tabs" role="tablist" aria-label="ޕްރޮޑަކްޓް">
        {products.map((p, i) => {
          const on = i === active;
          return (
            <button
              key={p.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setActive(i)}
              className={`showcase-tab ${on ? 'is-active' : ''}`}
            >
              <Icon name={p.icon} size={18} fill={on} />
              <span className="head-dv text-base leading-none">{p.name}</span>
              {on && (
                <span
                  key={active}
                  className={`showcase-progress ${paused || reduce ? 'is-paused' : ''}`}
                  style={{ animationDuration: `${hold}ms` }}
                />
              )}
            </button>
          );
        })}
      </div>

      <BrowserFrame host={host}>
        <div key={active} className="showcase-screen h-full">
          {Screen ? <Screen /> : null}
        </div>
      </BrowserFrame>
    </div>
  );
}
